import ArrowBack from "@mui/icons-material/ArrowBack";
import {
	Box,
	CircularProgress,
	IconButton,
	List,
	ListItemButton,
	ListItemText,
	Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { forwardRef } from "react";

import { PANEL_MAX_HEIGHT, PANEL_WIDTH } from "./constants";
import { useFormEntityListbox } from "./FormEntityListboxContext";

type SlidingListboxProps = React.HTMLAttributes<HTMLElement> & { sx?: SxProps<Theme> };

export const SlidingListbox = forwardRef<HTMLUListElement, SlidingListboxProps>(function SlidingListbox(
	props,
	ref,
) {
	const { children, sx: listboxSx, ...other } = props;
	const {
		drillDownOpen,
		subPanelTitle,
		filteredSubFields,
		fieldCategory,
		subPanelLoading,
		subPanelError,
		searchQuery,
		selectedValue,
		onBack,
		onFieldSelect,
	} = useFormEntityListbox();

	return (
		<Box sx={{ overflow: "hidden", width: PANEL_WIDTH, minHeight: 0 }}>
			<Box
				sx={{
					display: "flex",
					width: "200%",
					minHeight: 0,
					transform: drillDownOpen ? "translateX(-50%)" : "translateX(0%)",
					transition: "transform 280ms cubic-bezier(0.4, 0, 0.2, 1)",
				}}
			>
				<Box
					component="ul"
					ref={ref}
					{...other}
					sx={[
						...(listboxSx != null
							? Array.isArray(listboxSx)
								? listboxSx
								: [listboxSx]
							: []),
						{
							width: "50%",
							maxHeight: PANEL_MAX_HEIGHT,
							minHeight: 0,
							overflowY: "auto",
							overflowX: "hidden",
							WebkitOverflowScrolling: "touch",
							py: 1,
							listStyle: "none",
							margin: 0,
							padding: 0,
							flexShrink: 0,
							// While the table drill-down is open, ignore pointer hits on the main list.
							// Otherwise the same row index in the main listbox can receive the click and
							// Autocomplete commits the wrong option (parent index === sub-row index).
							pointerEvents: drillDownOpen ? "none" : "auto",
						},
					]}
				>
					{children}
				</Box>
				<Box
					sx={{
						width: "50%",
						maxHeight: PANEL_MAX_HEIGHT,
						minHeight: 0,
						overflowY: "auto",
						overflowX: "hidden",
						WebkitOverflowScrolling: "touch",
						flexShrink: 0,
						pointerEvents: "auto",
					}}
				>
					{drillDownOpen && (
						<>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									px: 1,
									py: 0.75,
									borderBottom: "1px solid",
									borderColor: "divider",
									position: "sticky",
									top: 0,
									backgroundColor: "background.paper",
									zIndex: 1,
								}}
							>
								<IconButton size="small" aria-label="Back to entity list" onClick={onBack} sx={{ mr: 0.5 }}>
									<ArrowBack fontSize="small" />
								</IconButton>
								<Typography
									variant="subtitle2"
									noWrap
									sx={{
										fontSize: 13,
										letterSpacing: "0.02em",
										color: "rgba(13, 31, 48, 0.86)",
										fontWeight: 600,
									}}
								>
									{subPanelTitle}
								</Typography>
							</Box>
							<List dense disablePadding sx={{ py: 1 }}>
								{subPanelLoading ? (
									<Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 3, py: 2 }}>
										<CircularProgress size={18} />
										<Typography variant="caption" color="text.secondary">
											Loading...
										</Typography>
									</Box>
								) : filteredSubFields.length > 0 ? (
									filteredSubFields.map((field) => (
										<ListItemButton
											key={field.value}
											component="div"
											selected={field.value === selectedValue}
											onMouseDown={(e) => {
												e.preventDefault();
												e.stopPropagation();
												onFieldSelect(field, fieldCategory);
											}}
											onClick={(e) => {
												e.preventDefault();
												e.stopPropagation();
											}}
											sx={{ pl: 3, py: 0.75 }}
										>
											<ListItemText
												primary={
													<Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", pr: 1 }}>
														<Typography variant="body2" component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
															{field.label}
														</Typography>
														{field.dataType && (
															<Typography
																component="span"
																variant="caption"
																sx={{
																	ml: "auto",
																	flexShrink: 0,
																	px: 0.875,
																	py: 0.25,
																	borderRadius: 1,
																	// Higher-contrast badge: solid 6% teal
																	// tint with 1px border and a darker text
																	// color than the prior `text.secondary`
																	// (which was failing on pale rows).
																	backgroundColor: "rgba(2, 213, 171, 0.10)",
																	border: "1px solid rgba(2, 213, 171, 0.32)",
																	color: "rgba(13, 31, 48, 0.78)",
																	fontWeight: 600,
																	fontSize: "0.7rem",
																	lineHeight: 1.4,
																	whiteSpace: "nowrap",
																	letterSpacing: "0.01em",
																}}
															>
																{field.dataType}
															</Typography>
														)}
													</Box>
												}
											/>
										</ListItemButton>
									))
								) : (
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: "block", px: 3, py: 1 }}
									>
										{subPanelError
											? "Error loading fields"
											: searchQuery
												? "No matching fields"
												: "No fields available"}
									</Typography>
								)}
							</List>
						</>
					)}
				</Box>
			</Box>
		</Box>
	);
});
